const KARSTEN = "#132749";
const KARSTEN_SOFT = "#7EA7E6";
const TRUSSARDI = "#3D2914";
const TRUSSARDI_SOFT = "#C08F5A";
const SECONDARY = "#007085";
const TEXT = "hsla(193, 7%, 27%, 1)";
const TEXT_LIGHT = "hsla(225, 14%, 54%, 1)";
const BORDER = "hsla(216, 31%, 93%, 1)";
const MUTED = "hsla(216, 29%, 97%, 1)";
const RADIUS = 10;

const styles = () => ({
  "@global": {
    "@keyframes kntFadeUp": {
      from: { opacity: 0, transform: "translateY(16px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    "@keyframes kntAccentSweep": {
      from: { transform: "scaleX(0)", transformOrigin: "left center" },
      to: { transform: "scaleX(1)", transformOrigin: "left center" },
    },
  },
  page: {
    minHeight: "100vh",
    fontFamily: "'Alexandria', sans-serif",
    color: TEXT,
    backgroundColor: "#f7f9fc",
    backgroundImage: `
      radial-gradient(ellipse 80% 50% at 0% -10%, rgba(126, 167, 230, 0.28), transparent 55%),
      radial-gradient(ellipse 60% 40% at 100% 0%, rgba(192, 143, 90, 0.14), transparent 50%),
      linear-gradient(180deg, #eef3fa 0%, #f7f9fc 28%, #ffffff 100%)
    `,
    backgroundAttachment: "fixed",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    backgroundColor: "rgba(247, 249, 252, 0.82)",
    borderBottom: `1px solid ${BORDER}`,
  },
  headerInner: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: "18px 20px 14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 10,
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  brandWordmark: {
    fontFamily: "'Alexandria', sans-serif",
    fontWeight: 600,
    fontSize: 22,
    letterSpacing: 1,
    color: KARSTEN,
    lineHeight: 1,
    margin: 0,
  },
  brandPlus: {
    color: TRUSSARDI,
  },
  brandSignature: {
    display: "block",
    marginTop: 4,
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: 2,
    color: TRUSSARDI,
    opacity: 0.6,
  },
  accentBar: {
    width: 180,
    height: 3,
    borderRadius: 999,
    background: `linear-gradient(90deg, ${KARSTEN_SOFT} 0%, ${KARSTEN} 50%, ${TRUSSARDI_SOFT} 100%)`,
    animation: "kntAccentSweep 700ms ease-out both",
  },
  headerCopy: {
    marginTop: 2,
  },
  headerTitle: {
    fontFamily: "'Alexandria', sans-serif",
    fontWeight: 600,
    fontSize: 18,
    color: KARSTEN,
    margin: 0,
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontFamily: "'Alexandria', sans-serif",
    fontWeight: 400,
    fontSize: 13,
    color: TEXT_LIGHT,
    margin: "4px 0 0",
    lineHeight: 1.45,
  },
  mobileNav: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    padding: "0 20px 12px",
    maxWidth: 1080,
    margin: "0 auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    "@media (min-width: 900px)": {
      display: "none",
    },
  },
  chip: {
    flexShrink: 0,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: TEXT_LIGHT,
    borderRadius: 999,
    padding: "6px 12px",
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 12,
    fontWeight: 500,
    textDecoration: "none",
    transition: "all 180ms ease",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: KARSTEN_SOFT,
      color: KARSTEN,
    },
  },
  chipActive: {
    borderColor: KARSTEN,
    backgroundColor: "rgba(19, 39, 73, 0.06)",
    color: KARSTEN,
    fontWeight: 600,
  },
  layout: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: "28px 20px 72px",
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 28,
    animation: "kntFadeUp 520ms ease-out both",
    "@media (min-width: 900px)": {
      gridTemplateColumns: "200px minmax(0, 1fr)",
      gap: 40,
      paddingTop: 40,
    },
  },
  sidebar: {
    display: "none",
    "@media (min-width: 900px)": {
      display: "block",
      position: "sticky",
      top: 140,
      alignSelf: "start",
    },
  },
  sidebarLabel: {
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: TEXT_LIGHT,
    margin: "0 0 12px",
  },
  sidebarList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    borderLeft: `2px solid ${BORDER}`,
  },
  sidebarLink: {
    display: "block",
    padding: "8px 0 8px 14px",
    marginLeft: -2,
    borderLeft: "2px solid transparent",
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: TEXT_LIGHT,
    textDecoration: "none",
    transition: "all 180ms ease",
    "&:hover": {
      color: KARSTEN,
    },
  },
  sidebarLinkActive: {
    borderLeftColor: KARSTEN,
    color: KARSTEN,
    fontWeight: 600,
  },
  feed: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    minWidth: 0,
  },
  article: {
    scrollMarginTop: 148,
    paddingBottom: 40,
    marginBottom: 8,
    borderBottom: `1px solid ${BORDER}`,
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  articleMeta: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10,
    marginBottom: 10,
  },
  versionBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: RADIUS,
    backgroundColor: "rgba(19, 39, 73, 0.06)",
    border: `1px solid rgba(19, 39, 73, 0.12)`,
    color: KARSTEN,
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.3,
  },
  latestDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: SECONDARY,
  },
  date: {
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 12,
    fontWeight: 400,
    color: TEXT_LIGHT,
  },
  articleTitle: {
    fontFamily: "'Alexandria', sans-serif",
    fontWeight: 600,
    fontSize: 22,
    color: KARSTEN,
    margin: "0 0 18px",
    lineHeight: 1.35,
    "@media (min-width: 600px)": {
      fontSize: 26,
    },
  },
  content: {
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 15,
    lineHeight: 1.7,
    color: TEXT,
    "& p": {
      margin: "0 0 12px",
    },
    "& p:last-child": {
      marginBottom: 0,
    },
    "& ul, & ol": {
      margin: "0 0 14px",
      paddingLeft: 22,
    },
    "& li": {
      marginBottom: 8,
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: RADIUS,
      display: "block",
      margin: "16px 0",
    },
    "& a": {
      color: SECONDARY,
      fontWeight: 500,
    },
    "& strong": {
      fontWeight: 600,
    },
    "& code, & span[style*='rgb(80, 250, 123)']": {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      fontSize: "0.92em",
    },
    "& hr, & p > span[style*='rgb(98, 114, 164)']": {
      opacity: 0.85,
    },
  },
  stateWrap: {
    minHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: 32,
    gap: 12,
  },
  stateTitle: {
    fontFamily: "'Alexandria', sans-serif",
    fontWeight: 600,
    fontSize: 20,
    color: KARSTEN,
    margin: 0,
  },
  stateText: {
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 14,
    color: TEXT_LIGHT,
    margin: 0,
    maxWidth: 360,
    lineHeight: 1.5,
  },
  spinner: {
    color: KARSTEN,
  },
  footer: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: "0 20px 40px",
    fontFamily: "'Alexandria', sans-serif",
    fontSize: 11,
    color: TEXT_LIGHT,
    letterSpacing: 0.4,
  },
});

export default styles;
export { KARSTEN, KARSTEN_SOFT, TRUSSARDI, TRUSSARDI_SOFT, SECONDARY, MUTED };
